import clsx from "clsx";
import { FC, useEffect, useState } from "react";
import { i18n, Numeric, Slider } from "rujira.ui";
import {
  BalanceCompact,
  usePreloadedBalance,
} from "../../common/components/Balance";
import {} from "../../portfolio/utils";

const adjust = (v: bigint, r: number) =>
  (BigInt(Math.floor(r * 100)) * v) / 100n;

export const InputQuantity: FC<{
  amount: bigint;
  setAmount: (v: bigint) => void;
  children?: React.ReactNode;
  className?: string;
}> = ({ amount, setAmount, children, className }) => {
  const { balance } = usePreloadedBalance();
  const max = balance?.balance || 0n;

  return (
    <div className="flex dir-c gap-y-1">
      <BalanceCompact
        className={className}
        onClick={(x) => setAmount(x.balance)}
      />
      <Numeric
        amount={amount}
        onChangeAmount={(amount) => {
          setAmount(amount);
        }}
        className="numeric-input--white">
        {children || <i18n.label>Qty.</i18n.label>}
      </Numeric>
      <div className="flex ai-c">
        <a
          className="fs-12 condensed fw-500 color-grey mr-0.5 pointer"
          onClick={() => setAmount(0n)}>
          0%
        </a>

        <ChunkSlider amount={amount} setAmount={setAmount} max={max} />
        <a
          className="fs-12 condensed fw-500 color-grey ml-0.5 pointer"
          onClick={() => setAmount(max)}>
          100%
        </a>
      </div>
    </div>
  );
};

export const ChunkSlider: FC<{
  amount: bigint;
  setAmount: (v: bigint) => void;
  max: bigint;
}> = ({ amount, setAmount, max }) => {
  const slider = max ? (amount * 100n) / max : 0n;
  const [temp, setTemp] = useState(Number(slider));

  useEffect(() => {
    const slider = max ? (amount * 100n) / max : 0n;
    setTemp(Number(slider));
  }, [slider, amount, max]);

  return (
    <Slider
      min={0}
      max={100}
      step={5}
      value={Number(slider)}
      onAfterChange={(v) => {
        setAmount(adjust(max, v / 100));
      }}
      onChange={(v) => {
        setTemp(v);
      }}
      marks={max ? [0, 25, 50, 75, 100] : false}
      renderMark={(props) => {
        const { className, key, ...rest } = props;
        return (
          <span
            key={key}
            className={clsx({
              slider__mark: true,
              [`slider__mark--${props.key}`]: true,
              "slider__mark--completed":
                Number(key) <= temp && Number(max) >= Number(key),
            })}
            {...rest}
          />
        );
      }}
    />
  );
};

/* Convert decimal bigint to strin decimal value without float arithmetic */
export const toDecimal = (v: BigInt, decimals = 12): string => {
  const s = v.toString();
  const len = s.length;

  if (len <= decimals) {
    const padded = s.padStart(decimals, "0");
    return `0.${padded}`;
  }

  const intPart = s.slice(0, len - decimals);
  const fracPart = s.slice(len - decimals).replace(/0+$/, ""); // trim trailing zeroes
  return fracPart.length ? `${intPart}.${fracPart}` : intPart;
};
